import { useQuery } from "@apollo/client";
import { MagnifyingGlassIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Loader from "../../components/Loader/Loader";
import Page from "../../components/Page/Page";
import Panel from "../../components/Panel/Panel";
import Badge from "../../components/Styled/Badge";
import TextInput from "../../components/TextInput/TextInput";
import { DeletedUser } from "../../graphql/graphql";
import { DELETED_USERS } from "../../graphql/queries-gql";
import { timeSinceString } from "../../util/helpers";

const SearchBox = styled.div`
  width: 320px;
  max-width: 100%;
`;

const UserRow = styled(Link)`
  display: grid;
  grid-template-columns: minmax(160px, 1fr) minmax(0, 2fr) auto;
  align-items: start;
  gap: 4px 16px;
  padding: 12px 18px;
  border-bottom: 1px solid ${(props) => props.theme.border};
  color: inherit;
  text-decoration: none;
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background-color: ${(props) => props.theme.surfaceHover};
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr auto;
  }
`;

const Identity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Username = styled.span`
  font-size: 13.5px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserId = styled.span`
  font-family: ${(props) => props.theme.monoFontFamily};
  font-size: 11.5px;
  color: ${(props) => props.theme.mutedTextColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Reason = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;

  @media (max-width: 640px) {
    grid-column: 1 / -1;
    grid-row: 2;
  }
`;

const DeletedText = styled.span`
  font-size: 13px;
  color: ${(props) => props.theme.secondaryTextColor};
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const LastActive = styled.span`
  font-size: 12.5px;
  color: ${(props) => props.theme.secondaryTextColor};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const DeletedUsers = () => {
  const { loading, data, error } = useQuery<{ deletedUsers: DeletedUser[] }>(
    DELETED_USERS,
    {
      fetchPolicy: "network-only",
    }
  );
  const [filter, setFilter] = useState("");

  const deletedUsers = useMemo(() => {
    const all = data?.deletedUsers ?? [];
    const needle = filter.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((user) =>
      [user._id, user.username, user.deletedReason, user.deletedText].some(
        (value) => value?.toLowerCase().includes(needle)
      )
    );
  }, [data?.deletedUsers, filter]);

  return (
    <Page
      title="Deleted users"
      badge={
        data?.deletedUsers && <Badge>{data.deletedUsers.length} total</Badge>
      }
      toolbar={
        <SearchBox>
          <TextInput
            allowEmpty={true}
            onChange={(value) => setFilter(value)}
            placeHolder={"Filter by name, id or reason"}
            icon={<MagnifyingGlassIcon width={18} />}
          />
        </SearchBox>
      }
    >
      {loading && <Loader />}
      {error && <ErrorAlert error={error} />}

      {data?.deletedUsers && deletedUsers.length === 0 && (
        <EmptyState
          Icon={TrashIcon}
          title={filter ? "No matching users" : "No deleted users"}
        />
      )}

      {deletedUsers.length > 0 && (
        <Panel padding={0}>
          {deletedUsers.map((user) => {
            return (
              <UserRow key={user._id} to={`/user/${user._id}`}>
                <Identity>
                  <Username>{user.username}</Username>
                  <UserId>{user._id}</UserId>
                </Identity>
                <Reason>
                  {user.deletedReason && (
                    <Badge>{user.deletedReason}</Badge>
                  )}
                  {user.deletedText && (
                    <DeletedText>{user.deletedText}</DeletedText>
                  )}
                </Reason>
                <LastActive title="Last active">
                  {timeSinceString(user.lastActive)}
                </LastActive>
              </UserRow>
            );
          })}
        </Panel>
      )}
    </Page>
  );
};
export default DeletedUsers;
